import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Droplets, Sparkles, Home as HomeIcon } from "lucide-react";
import { HERO_IMG, WHATSAPP_LINK } from "@/config";
import { WaIcon } from "./Nav";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.15 + i * 0.14, ease: [0.19, 1, 0.22, 1] },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

const CHIPS = [
  { icon: HomeIcon, label: "Vamos hasta tu garaje" },
  { icon: Droplets, label: "Agua y luz propias" },
  { icon: Sparkles, label: "Productos premium" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
      <div
        className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-aqua/10 blur-[140px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-gold/5 blur-[120px]"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-5 md:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7 min-w-0">
          <motion.p
            {...fade(0.05)}
            className="inline-flex items-center gap-2.5 font-mono text-[11px] md:text-xs tracking-[0.25em] text-aqua uppercase"
          >
            <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-aqua" />
            Detailing a domicilio
          </motion.p>

          <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-[-0.02em] text-[clamp(2rem,4.5vw,4rem)]">
            {["El brillo", "que tu coche", "merece."].map((l, i) => (
              <span key={l} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-outline" : ""}`}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  custom={i}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p {...fade(0.75)} className="mt-6 max-w-xl text-base md:text-lg text-white/60 leading-relaxed">
            Lavado y detailing profesional donde esté tu coche: en casa, en la oficina o en tu garaje.
            Nosotros llevamos el agua, la luz y el brillo.
          </motion.p>

          <motion.div {...fade(0.9)} className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-cta"
              className="inline-flex items-center gap-2.5 rounded-full bg-wa px-7 py-4 text-base font-semibold text-[#06120b] transition-all duration-300 hover:brightness-110 hover:shadow-[0_0_36px_rgba(37,211,102,0.45)]"
            >
              <WaIcon size={18} />
              Pedir cita por WhatsApp
            </a>
            <a
              href="#servicios"
              data-testid="hero-services-cta"
              onClick={(e) => {
                e.preventDefault();
                if (window.__lenis) window.__lenis.scrollTo("#servicios", { offset: -90 });
                else document.querySelector("#servicios")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 text-base text-white/80 transition-all duration-300 hover:border-aqua/60 hover:text-aqua"
            >
              Ver servicios
            </a>
          </motion.div>

          <motion.div {...fade(1.05)} className="mt-10 flex flex-wrap gap-3">
            {CHIPS.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm text-white/70"
              >
                <Icon size={15} className="text-aqua" />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 min-w-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl border border-white/10 aspect-[4/5]">
              <motion.img
                src={HERO_IMG}
                alt="Coche con brillo especular tras el detailing"
                style={{ y, scale: 1.18 }}
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-[0.2em] text-white/70 uppercase">
                  Wet look — acabado espejo
                </span>
                <span className="h-px flex-1 mx-4 bg-gradient-to-r from-transparent via-aqua/60 to-transparent" />
              </div>
            </div>
            <div
              className="absolute -inset-3 rounded-[2rem] border border-aqua/10 -z-10"
              aria-hidden="true"
            />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="hidden lg:flex absolute bottom-8 left-8 items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase"
      >
        <span className="inline-block h-8 w-px bg-gradient-to-b from-aqua to-transparent" />
        scroll
      </motion.div>
    </section>
  );
};

export default Hero;
